/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { dataEqual } from "@adapptio/lib-blueprint";
import {
	IAccount,
	IClientSession,
} from "@adapptio/lib-core";
import { createEventEmitter, emitEvent, offEvent, onEvent } from "@adapptio/lib-shared";

import { IEditorApiService } from "../api/IEditorApiService";
import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";
import { ErrorTracker } from "@adapptio/lib-components";

const SESSION_RELOAD_INTERVAL = 30000;

/**
 * Service to manage environment
 */
export class EnvironmentService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** API Service Instance */
	private apiService: IEditorApiService;

	/* Reload timer refs */
	private envListReloadTimer: unknown;
	private sessionReloadTimer: unknown;

	/** Currently configured account ID */
	private currentAccountId: string = null;

	/** Session object */
	private session: IClientSession = null;

	/** User object */
	private identity: IAccount = null;

	/** Emitted when session changed */
	public onSessionChange = createEventEmitter<void>();

	private handleApiOnAuthorize: () => void;

	/**
	 * Resource Service Constructor
	 *
	 * @param dc Dependency Container
	 */
	public constructor(dc: IEditor) {
		this.apiService = dc.get("apiService");
		this.logger = dc.get("logger").facility("EnvironmentService");
	}

	/**
	 * Initializes service
	 */
	public async init(): Promise<void> {
		await this.loadSession();
		this.handleSessionReload(false);

		this.handleApiOnAuthorize = () => {
			this.handleSessionReload(true);
		}

		this.autologinAppUser().catch(() => {
			// Ignore
		});

		onEvent(this.apiService.onAuthorize, this.handleApiOnAuthorize);
	}

	/**
	 * Disposes service
	 */
	public async dispose(): Promise<void> {
		offEvent(this.apiService.onAuthorize, this.handleApiOnAuthorize);

		if (this.envListReloadTimer) {
			clearTimeout(this.envListReloadTimer as number);
		}

		if (this.sessionReloadTimer) {
			clearTimeout(this.sessionReloadTimer as number);
		}
	}

	/**
	 * Handles session reload timer
	 */
	private handleSessionReload(load = true) {
		if (load) {
			this.loadSession();
		}

		this.sessionReloadTimer = setTimeout(() => this.handleSessionReload(), SESSION_RELOAD_INTERVAL);
	}

	/**
	 * Loads session object
	 */
	private async loadSession(reloadUserAccount?: boolean) {
		try {
			const res = await this.apiService.getSession(reloadUserAccount);

			const session = {
				sessionId: res.sessionId,
				locale: res.locale,
				meta: res.meta,
				identityKey: res.identityKey
			};

			const identity = res.identity;

			if (!dataEqual(this.session, session) || !dataEqual(this.identity, identity)) {
				this.session = session;
				this.setIdentity(identity);

				this.logger.debug("Session updated:", this.session, this.identity);
				emitEvent(this.onSessionChange);
			}
		} catch (err) {
			this.logger.warn("Failed to load session:", err);
		}
	}

	/**
	 * Returns session object
	 */
	public getSession(): IClientSession {
		return this.session;
	}

	/**
	 * Sets user object
	 */
	public setIdentity(newIdentity: IAccount): void {
		this.identity = newIdentity;

		const errorTracker = ErrorTracker.getInstance();

		if (newIdentity?.accountId || newIdentity?.email) {
			errorTracker.setUser({
				...newIdentity,
				id: newIdentity.accountId,
				email: newIdentity.email,
				username: newIdentity.fullName || newIdentity.email,
				name: newIdentity.fullName,
				ip_address: "{{auto}}",
				segment: newIdentity.jobRole,
				admin: newIdentity.isAdmin
			});
		} else {
			errorTracker.clearUser();
		}
	}

	public async autologinAppUser(): Promise<void> {
		await this.apiService.autologinAppUser();
		await this.reloadSession(true);
	}

	/**
	 * Returns user object
	 */
	public getIdentity(): IAccount {
		return this.identity;
	}

	/**
	 * Reloads session
	 */
	public async reloadSession(reloadUserAccount?: boolean): Promise<void> {
		await this.loadSession(reloadUserAccount);
	}
}
